import type { Peer } from "@/api/generated/model";
import type { ParticipantRow } from "@/components/dashboard/ParticipantsOverviewTable";

export interface ModuleParticipantBucket {
  participant: ParticipantRow;
  progressPercent: number;
  currentSubchapterTitle: string | null;
}

export interface ModuleAggregate {
  index: number;
  title: string;
  completed: ModuleParticipantBucket[];
  inProgress: ModuleParticipantBucket[];
  notStarted: ModuleParticipantBucket[];
}

const peerAverageProgress = (peer: Peer): number => {
  if (peer.modules.length === 0) return 0;
  return (
    peer.modules.reduce((sum, m) => sum + m.progressPercent, 0) /
    peer.modules.length
  );
};

export const groupAverageProgress = (peers: Peer[]): number => {
  if (peers.length === 0) return 0;
  return Math.round(
    peers.reduce((sum, p) => sum + peerAverageProgress(p), 0) / peers.length
  );
};

export const aggregateModuleStats = (
  peers: Peer[],
  participants: ParticipantRow[]
): ModuleAggregate[] => {
  const canonical = peers.find((p) => p.modules.length > 0)?.modules ?? [];
  const byUserId = new Map(participants.map((p) => [p.userId, p]));

  return canonical.map((mod, i) => {
    const aggregate: ModuleAggregate = {
      index: i + 1,
      title: mod.title,
      completed: [],
      inProgress: [],
      notStarted: [],
    };

    for (const peer of peers) {
      const participant = byUserId.get(peer.userId);
      if (!participant) continue;
      const m = peer.modules.find((x) => x.title === mod.title);
      const pct = m?.progressPercent ?? 0;
      const isCurrentModule = peer.currentModuleTitle === mod.title;
      const bucket: ModuleParticipantBucket = {
        participant,
        progressPercent: pct,
        currentSubchapterTitle: isCurrentModule
          ? peer.currentSubchapterTitle
          : null,
      };
      if (pct >= 100) aggregate.completed.push(bucket);
      else if (pct > 0) aggregate.inProgress.push(bucket);
      else aggregate.notStarted.push(bucket);
    }

    return aggregate;
  });
};
